/**
 * Tiny pub/sub for live-previewing PI profile edits across the split between
 * the sidebar form and the ProfileTab iframe host.
 *
 * The form publishes the current draft on every change; the ProfileTab
 * subscribes and postMessages the draft into the studien-netzwerk iframe.
 * Module-level state is fine here — only one Profile tab can be active at a
 * time, and the published draft is replaced on every keystroke.
 */

export interface ClinicDraftRef {
  clinicId: string;
  slug: string;
  name: string;
}

export interface ProfileDraftPreview {
  slug: string;
  bio: string | null;
  groupName: string | null;
  groupDescription: string | null;
  photoUrl: string | null;
  /** Primary phone. `null` means "unchanged" in this draft. */
  phone?: string | null;
  /** Full replacement of the additional phone list. `null` means "unchanged". */
  additionalPhones?: string[] | null;
  /** Full replacement of the clinic affiliations. `null` means "unchanged". */
  clinicRefs?: ClinicDraftRef[] | null;
}

type Subscriber = (draft: ProfileDraftPreview | null) => void;

const subscribers = new Set<Subscriber>();
let current: ProfileDraftPreview | null = null;

export function publishProfileDraft(draft: ProfileDraftPreview | null): void {
  current = draft;
  for (const fn of subscribers) fn(draft);
}

export function subscribeProfileDraft(fn: Subscriber): () => void {
  subscribers.add(fn);
  // Replay latest so a late subscriber doesn't miss the current draft.
  if (current) fn(current);
  return () => {
    subscribers.delete(fn);
  };
}

export function clearProfileDraft(): void {
  publishProfileDraft(null);
}
